import React, {useEffect, useState} from 'react';
import {connect} from 'react-redux';
import {useForm, useFormState} from 'react-final-form';

import {Divider, Grid, Typography} from '@mui/material';
import makeStyles from '@mui/styles/makeStyles';

import {Dispatch, Store} from 'spectra-logic-ui';
import {fetchResource} from 'spectra-logic-ui/actions';
import {Checkbox, SingleSelect, TextField, WizardPage} from 'spectra-logic-ui/components';

import {ActionType, BucketOwnershipControls, FormActionType} from '@/enum';
import {Account, Action, Bucket, UserAction} from '@/types';
import DialogDescription from '@/components/form/dialog_description';
import {ActionSummary} from '@/iam/users/details/actions';

type PageProps = {
  title: string;
  action: FormActionType;
  bucket?: Bucket;
  accounts: Account[];
  validate: (formValues: any) => object;
  actions?: Action[];
  userActions?: UserAction[];
  fetchActions?: () => Promise<any>;
  fetchUserActions?: (accountId: string) => Promise<any>;
}

const useControlTipStyles = makeStyles({
  divider: {
    margin: '10px 0',
  },
  option: {
    marginBottom: 10,
  },
  optionHeader: {
    fontStyle: 'italic',
    marginBottom: 2,
  },
  optionContent: {
    display: 'flex',
    alignItems: 'center',
    '& > *': {
      marginLeft: 10,
    },
  },
  optionItem: {
    display: 'flex',
    alignItems: 'center',
  },
});

export const controlTranslations = {
  [BucketOwnershipControls.BUCKET_OWNER_ENFORCED]: 'ACLs Disabled',
  [BucketOwnershipControls.BUCKET_OWNER_PREFERRED]: 'Bucket Owner Preferred',
  [BucketOwnershipControls.OBJECT_WRITER]: 'Object Writer',
};

export const ControlTip = () => {
  const classes = useControlTipStyles();

  return (
    <div>
      <Typography variant='body2'>
        Determines ownership of new objects and how Access Control Lists (ACLs) are used.
      </Typography>
      <Divider className={classes.divider} />
      <div className={classes.option}>
        <div className={classes.optionHeader}>
          <Typography variant='body2' display='inline'>
            <strong>{controlTranslations[BucketOwnershipControls.BUCKET_OWNER_ENFORCED]}</strong>
          </Typography>
        </div>
        <div className={classes.optionContent}>
          <Typography variant='body2' display='inline'>
            ACLs are disabled. New objects written to this bucket are always owned by the bucket owner.
            Access to this bucket and its objects is specified using only policies.
            This is the recommended setting.
          </Typography>
        </div>
      </div>
      <div className={classes.option}>
        <div className={classes.optionHeader}>
          <Typography variant='body2' display='inline'>
            <strong>{controlTranslations[BucketOwnershipControls.BUCKET_OWNER_PREFERRED]}</strong>
          </Typography>
        </div>
        <div className={classes.optionContent}>
          <Typography variant='body2' display='inline'>
            If new objects written to this bucket specify the <i>bucket-owner-full-control</i> canned ACL,
            they are owned by the bucket owner. Otherwise, they are owned by the object writer.
            Access to this bucket and its objects can be specified using either ACLs or policies.
          </Typography>
        </div>
      </div>
      <div className={classes.option}>
        <div className={classes.optionHeader}>
          <Typography variant='body2' display='inline'>
            <strong>{controlTranslations[BucketOwnershipControls.OBJECT_WRITER]}</strong>
          </Typography>
        </div>
        <div className={classes.optionContent}>
          <Typography variant='body2' display='inline'>
            The object writer remains the object owner. Access to this bucket and its objects can be
            specified using either ACLs or policies.
          </Typography>
        </div>
      </div>
    </div>
  );
};

const lockingTooltip = `
Enables object locking on this bucket. Versioning must also be enabled in order to enable this.
`;

const compressTooltip = `
Enabling compression reduces storage requirements when objects placed in the
bucket are compressible. Compression is not recommended for objects with
non-compressible data (e.g. ZIP, media files), since it can negatively impact
performance.
`;

const archiveTooltip = `
AWS S3 Glacier restore operations can be hidden to allow clients that do
not fully support Glacier to still operate. This setting automatically
initiates a restore when a request is made for an object that requires
it. A service unavailable (503) error is returned instead of the standard
InvalidObjectState (403) error. This setting interferes with the operation
of clients that properly support AWS S3 Glacier restores.
`;

export const UserActionTip = ({action, userAction}: {action?: Action; userAction?: UserAction}) => {
  if (!action && !userAction) {
    return <p>Enabling a user action will cause that action to be executed after the bucket is created</p>;
  }

  return (
    <>
      <p>The following user action will be executed after the bucket is created:</p>
      <ActionSummary action={action} userAction={userAction} />
    </>
  );
};

const ParametersPage = ({title, action, bucket, accounts, actions=[], userActions=[],
  fetchActions, fetchUserActions, ...others}: PageProps) => {
  const form = useForm();
  const formState = useFormState();
  const locking = formState.values.locking;

  const description = action == FormActionType.EDIT ? 'Edit bucket parameters.' : 'Configure your new bucket.';

  const actionsMap: Record<string, Action> = {};
  actions.forEach((action: Action) => {
    actionsMap[action.id] = action;
  });

  const [selectedUserActionId, setSelectedUserActionId] = useState('');
  let selectedUserAction: UserAction | undefined;
  if (selectedUserActionId) {
    selectedUserAction = userActions.find((ua) => ua.id === selectedUserActionId);
  }
  const changeUserActionId = (id: string) => {
    setSelectedUserActionId(id);
    const userAction = userActions.find((ua) => ua.id === id);
    form.mutators.setFormValue('actionId', id);
    form.mutators.setFormValue('user', userAction ? userAction.user : undefined);
  };

  const handleVersioningChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const isChecked = e && e.target && e.target.checked;
    if (!isChecked && locking) {
      form.mutators.setFormValue('locking', false);
    }
  };

  const ownerMap: Record<string, string> = {};
  accounts.forEach((account) => ownerMap[account.canonicalId] = account.id);

  if (action === FormActionType.CREATE) {
    useEffect(() => {
      if (fetchActions) {
        fetchActions();
      }
      const accountId = ownerMap[formState.values.owner];
      if (accountId && fetchUserActions) {
        // Fetch user actions when the page is first rendered.
        fetchUserActions(accountId);
      }
    }, []);
  }

  const handleOwnerChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newOwner = (e && e.target) ? e.target.value : '';
    if (action === FormActionType.CREATE && newOwner) {
      const newAccountId = ownerMap[newOwner];
      if (newAccountId && fetchUserActions) {
        // Fetch user actions when the owner is changed.
        fetchUserActions(newAccountId);
      }
      form.mutators.setFormValue('userActionId', undefined);
      changeUserActionId('');
    }
  };

  const controlOptions = [{
    key: BucketOwnershipControls.BUCKET_OWNER_ENFORCED,
    text: controlTranslations[BucketOwnershipControls.BUCKET_OWNER_ENFORCED] + ' (recommended)',
  }, {
    key: BucketOwnershipControls.BUCKET_OWNER_PREFERRED,
    text: controlTranslations[BucketOwnershipControls.BUCKET_OWNER_PREFERRED],
  }, {
    key: BucketOwnershipControls.OBJECT_WRITER,
    text: controlTranslations[BucketOwnershipControls.OBJECT_WRITER],
  }];

  const ownerOptions = [];
  for (let i = 0; i < accounts.length; i++) {
    const account = accounts[i];
    const option = {key: account.canonicalId, text: account.description || account.id};
    if (account.default) {
      ownerOptions.unshift(option);
    } else {
      ownerOptions.push(option);
    }
  }

  const userActionOptions = userActions.map((ua) => {
    const action = actionsMap[ua.id];
    let text = `IAM user "${ua.user}"`;
    text += `: ${action ? action.title : ua.id}`;
    if (action && action.version) {
      text += ` - Version ${action.version}`;
    }
    return {key: ua.id, text: text};
  });

  return (
    <WizardPage
      title={title} {...others}
      description={<DialogDescription>{description}</DialogDescription>}
    >
      <Grid container spacing={2}>
        <Grid item xs={12}>
          <TextField
            name='name' label='Bucket Name'
            disabled={action === FormActionType.EDIT}
          />
        </Grid>
        <Grid item xs={12}>
          <Checkbox name='versioningEnabled' label='Enable Versioning' disabled={bucket && bucket.locking}
            onChange={handleVersioningChange} />
          <Checkbox name='locking' label='Enable Object Locking' tooltip={lockingTooltip}
            disabled={!formState.values.versioningEnabled || (bucket && bucket.locking)} />
        </Grid>
        <Grid item xs={12}>
          <Checkbox name='encrypt' label='Enable Encryption' />
          <Checkbox name='compress' label='Enable Compression' tooltip={compressTooltip} />
          <Checkbox name='restore' label='Hide Glacier Operations' tooltip={archiveTooltip} />
        </Grid>
        <Grid item xs={6}>
          <SingleSelect name='owner' label='Bucket Owner' options={ownerOptions}
            onChange={handleOwnerChange} />
        </Grid>
        <Grid item xs={6}>
          <SingleSelect name='control' label='Object Ownership'
            options={controlOptions} tooltip={<ControlTip />} />
        </Grid>
        {action === FormActionType.CREATE && formState.values.owner && userActions.length > 0 &&
          <Grid item xs={12}>
            {userActions.length === 1 &&
              <Checkbox name='userActionId'
                label={actionsMap[userActions[0].id] ? actionsMap[userActions[0].id].title : userActions[0].id}
                onChange={(e) => {
                  if (e && e.target && e.target.checked) {
                    changeUserActionId(userActions[0].id);
                  } else {
                    changeUserActionId('');
                  }
                }}
                tooltip={<UserActionTip
                  action={actionsMap[selectedUserActionId]} userAction={selectedUserAction} />} />}
            {userActions.length > 1 &&
              <SingleSelect name='userActionId' label='User action to run on bucket creation'
                options={userActionOptions} noneOption
                onChange={(e) => {
                  if (e && e.target.value) {
                    changeUserActionId(e.target.value);
                  } else {
                    changeUserActionId('');
                  }
                }}
                tooltip={<UserActionTip
                  action={actionsMap[selectedUserActionId]} userAction={selectedUserAction} />} />}
          </Grid>
        }
      </Grid>
    </WizardPage>
  );
};

const userActionStoreName = 'createBucketUserActions';

const mapStateToProps = (state: Store) => {
  const actionsResource = state.resources.actions || {};
  const userActionsResource = state.resources[userActionStoreName] || {};
  return {
    actions: actionsResource.data,
    userActions: userActionsResource.data,
  };
};

const mapDispatchToProps = (dispatch: Dispatch) => ({
  fetchActions: () => dispatch(fetchResource('actions')),
  fetchUserActions: (accountId: string) => {
    const opts = {storeName: userActionStoreName};
    return dispatch(fetchResource(`actions/${accountId}/${ActionType.CREATE_BUCKET}`, undefined, opts));
  },
});

export default connect(mapStateToProps, mapDispatchToProps)(ParametersPage);
